import {
  ClipboardList,
  FileText,
  FlaskConical,
  Pill,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';

import type { EventoDoHistoricoResponse } from '@/api/generated/model';
import { cn } from '@/shared/lib/cn';
import { Badge } from '@/shared/ui/badge';

/** `Record` fechado: tipo novo no backend vira erro de compilação. */
const APARENCIA: Record<EventoDoHistoricoResponse['tipo'], { icone: LucideIcon; rotulo: string }> =
  {
    EXAME: { icone: FlaskConical, rotulo: 'Exame' },
    PRONTUARIO: { icone: FileText, rotulo: 'Prontuário' },
    REQUISICAO: { icone: ClipboardList, rotulo: 'Requisição' },
    RECEITUARIO: { icone: Pill, rotulo: 'Receituário' },
    // ⚠️ Ícone DIFERENTE do receituário, apesar de os dois falarem de remédio.
    // Receita é o que foi PRESCRITO e assinado; tratamento é o que está SENDO
    // FEITO — e os dois divergem na vida real (o médico manda dobrar a dose por
    // telefone, sem receita nova). Repetir o comprimido nos dois faria a linha
    // do tempo esconder exatamente a divergência que a Análise Comparativa
    // existe para enxergar.
    TRATAMENTO: { icone: Stethoscope, rotulo: 'Tratamento' },
  };

/**
 * Linha do tempo unificada (F6-1).
 *
 * A ordenação e a união das quatro origens vêm prontas do servidor — a tela só
 * desenha. Foi o que a V1 não fazia: ela buscava cada tabela no navegador e
 * concatenava, e o resultado mudava de ordem conforme a resposta chegava.
 */
interface Props {
  eventos: readonly EventoDoHistoricoResponse[];
  /**
   * Quando presente, o título de eventos EXAME vira botão em vez de texto —
   * hoje só o histórico do paciente dentro da discussão passa isto, pra abrir
   * o modal de resultados. A tela de histórico do médico não passa: lá o
   * evento já é o contexto principal da página, não precisa de um segundo
   * lugar pra clicar.
   */
  aoClicarExame?: (exameId: string) => void;
}

export function LinhaDoTempo({ eventos, aoClicarExame }: Props) {
  return (
    <ol className="relative space-y-0">
      {eventos.map((evento, indice) => {
        const { icone: Icone, rotulo } = APARENCIA[evento.tipo];
        const ultimo = indice === eventos.length - 1;

        return (
          <li key={`${evento.tipo}-${evento.id}`} className="relative flex gap-4 pb-6">
            {/* O fio vertical para no penúltimo — descendo do último item ele
                fica pendurado no vazio. */}
            {!ultimo && (
              <span
                className="absolute left-[19px] top-10 bottom-0 w-px bg-border"
                aria-hidden="true"
              />
            )}

            <span
              className={cn(
                'relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                'border-2 border-brand/20 bg-surface-raised',
              )}
            >
              <Icone className="h-4 w-4 text-brand" aria-hidden="true" />
            </span>

            <div className="flex-1 min-w-0 pt-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  {rotulo}
                </span>
                <time dateTime={evento.ocorridoEm} className="text-xs text-muted-foreground">
                  {new Date(evento.ocorridoEm).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </time>

                {evento.situacao === 'RASCUNHO' && (
                  <Badge variant="outline" className="font-normal">
                    Rascunho
                  </Badge>
                )}

                {evento.resultadosAlterados > 0 && (
                  <Badge variant="outline" className="text-amber-600 border-amber-600/40">
                    {evento.resultadosAlterados === 1
                      ? '1 resultado fora da faixa'
                      : `${evento.resultadosAlterados} resultados fora da faixa`}
                  </Badge>
                )}
              </div>

              {aoClicarExame && evento.tipo === 'EXAME' ? (
                <button
                  type="button"
                  onClick={() => aoClicarExame(evento.id)}
                  className="font-medium text-foreground hover:text-brand transition-colors text-left mt-0.5"
                >
                  {evento.titulo}
                </button>
              ) : (
                <p className="font-medium text-foreground mt-0.5">{evento.titulo}</p>
              )}

              {evento.descricao && (
                <p className="text-sm text-muted-foreground mt-0.5">{evento.descricao}</p>
              )}

              {evento.veterinarioNome && (
                <p className="text-xs text-muted-foreground mt-1">{evento.veterinarioNome}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
